import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Container,
  Paper,
  Button,
  IconButton,
  Chip,
  CircularProgress,
  Fade,
  TextField,
  Alert,
  Tooltip,
  Divider,
} from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import StarIcon from '@mui/icons-material/Star';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SendIcon from '@mui/icons-material/Send';
import {
  addfeedbackDispatch,
  getCustomerIdDispatch,
} from '../reducers/HomeReducer';
import { axiosInstance } from '../../src/Axios';
import './DropZoneFeedback.css';

// Safe confetti fallback via window.confetti
const confetti = (opts) => {
  if (typeof window !== 'undefined' && typeof window.confetti === 'function') {
    return window.confetti(opts);
  }
};

const QUICK_TAGS = [
  '⭐ Excellent Service',
  '🤝 Friendly Staff',
  '⚡ Super Fast',
  '💎 High Quality',
  '💰 Great Value',
  '🧼 Clean & Modern',
  '👍 Highly Recommended',
];

const TONE_OPTIONS = [
  { id: 'concise', label: 'Short & Sweet' },
  { id: 'balanced', label: 'Balanced' },
  { id: 'enthusiastic', label: 'Enthusiastic 🔥' },
];

const ReviewForm = () => {
  const { id, name } = useParams();
  const dispatch = useDispatch();
  const { cust } = useSelector((state) => state.home);

  const [rating, setRating] = useState(5);
  const [hoverRating, setHoverRating] = useState(0);

  // 4-5 Stars AI Review State
  const [selectedTags, setSelectedTags] = useState(['⭐ Excellent Service', '🤝 Friendly Staff']);
  const [selectedTone, setSelectedTone] = useState('concise');
  const [reviewText, setReviewText] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showRedirectNotice, setShowRedirectNotice] = useState(false);

  // 1-3 Stars Private Feedback State
  const [privateName, setPrivateName] = useState('');
  const [privatePhone, setPrivatePhone] = useState('');
  const [privateEmail, setPrivateEmail] = useState('');
  const [privateComment, setPrivateComment] = useState('');
  const [isSubmittingPrivate, setIsSubmittingPrivate] = useState(false);
  const [privateSubmitted, setPrivateSubmitted] = useState(false);

  const customer = cust?.result || {};
  const businessName = customer?.agency_name || customer?.firstname || name || 'Our Business';
  const googleReviewUrl = customer?.url || '';

  useEffect(() => {
    if (id) {
      dispatch(getCustomerIdDispatch(id));
    }
  }, [dispatch, id]);

  // Generate Review Callback
  const handleGenerateAi = useCallback(
    async (tags = selectedTags, tone = selectedTone) => {
      setIsGenerating(true);
      try {
        const cleanTags = tags.map((t) => t.replace(/^[^\w\s]+/, '').trim());
        const res = await axiosInstance.post('/reviews/generate-ai', {
          customer_id: id,
          business_name: businessName,
          tags: cleanTags,
          tone: tone,
          count: 1,
        });

        if (res.data?.reviews && res.data.reviews.length > 0) {
          setReviewText(res.data.reviews[0]);
          setIsGenerating(false);
          return;
        }
      } catch (err) {
        console.warn('AI generation fallback to template:', err);
      }

      // Fallback genuine short review
      setReviewText(
        `Great experience with ${businessName}! Extremely polite staff, quick turnaround, and top-notch service. Highly recommended!`
      );
      setIsGenerating(false);
    },
    [id, businessName, selectedTags, selectedTone]
  );

  // Generate review automatically on load or when rating is 4-5
  useEffect(() => {
    if (rating >= 4) {
      handleGenerateAi();
    }
  }, [rating, businessName]);

  const toggleTag = (tag) => {
    const updated = selectedTags.includes(tag)
      ? selectedTags.filter((t) => t !== tag)
      : [...selectedTags, tag];
    setSelectedTags(updated);
    handleGenerateAi(updated, selectedTone);
  };

  const handleToneChange = (tone) => {
    setSelectedTone(tone);
    handleGenerateAi(selectedTags, tone);
  };

  // Copy & Redirect to Google Maps
  const handleCopyAndPost = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(reviewText);
      }
      setCopied(true);
      setShowRedirectNotice(true);

      try {
        confetti({
          particleCount: 80,
          spread: 70,
          origin: { y: 0.6 },
          colors: ['#4285F4', '#34A853', '#FBBC05', '#EA4335'],
        });
      } catch (e) {}

      setTimeout(() => {
        if (googleReviewUrl) {
          window.location.href = googleReviewUrl;
        } else {
          window.open(
            `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(businessName)}`,
            '_blank'
          );
        }
      }, 1200);
    } catch (e) {
      if (googleReviewUrl) window.location.href = googleReviewUrl;
    }
  };

  // Submit Private Grievance (1-3 stars)
  const handlePrivateSubmit = (e) => {
    e.preventDefault();
    setIsSubmittingPrivate(true);
    const form = {
      name: privateName,
      phone: privatePhone,
      email: privateEmail,
      cust_id: id,
      api_key: customer?.apikey,
      desc: privateComment,
      rating: rating,
    };

    dispatch(addfeedbackDispatch(form));
    setTimeout(() => {
      setIsSubmittingPrivate(false);
      setPrivateSubmitted(true);
    }, 600);
  };

  return (
    <Container maxWidth="sm" sx={{ py: 4, minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
      <Paper
        elevation={4}
        sx={{
          borderRadius: 4,
          p: { xs: 3, sm: 4 },
          background: 'linear-gradient(180deg, #ffffff 0%, #f9fbff 100%)',
          border: '1px solid #e2e8f0',
          boxShadow: '0 20px 40px rgba(0,0,0,0.06)',
          textAlign: 'center',
        }}>
        {/* Business Header */}
        <Box display="flex" alignItems="center" justifyContent="center" gap={1.5} mb={1}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 3,
              bgcolor: '#1976d2',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 800,
              fontSize: '1.2rem',
              boxShadow: '0 4px 10px rgba(25, 118, 210, 0.3)',
            }}>
            {businessName.substring(0, 2).toUpperCase()}
          </Box>
          <Typography variant="h5" fontWeight="800" color="text.primary">
            {businessName}
          </Typography>
        </Box>

        <Typography variant="caption" color="text.secondary" display="block" mb={2}>
          Google Verified Partner • Customer Review Portal
        </Typography>

        {/* Quick Contact Icons */}
        {customer?.mobile && (
          <Box display="flex" justifyContent="center" gap={1.5} mb={3}>
            <Tooltip title="Call">
              <IconButton
                component="a"
                href={`tel:${customer.mobile}`}
                sx={{ bgcolor: '#f1f5f9', color: '#1976d2', '&:hover': { bgcolor: '#e2e8f0' } }}>
                <PhoneIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="WhatsApp">
              <IconButton
                component="a"
                href={`https://wa.me/${customer.mobile.replace(/\D/g, '')}?text=${encodeURIComponent(
                  `Hi ${businessName}, I would like to get in touch.`
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ bgcolor: '#f1f5f9', color: '#25D366', '&:hover': { bgcolor: '#e2e8f0' } }}>
                <WhatsAppIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {customer?.email && (
              <Tooltip title="Email">
                <IconButton
                  component="a"
                  href={`mailto:${customer.email}`}
                  sx={{ bgcolor: '#f1f5f9', color: '#ea4335', '&:hover': { bgcolor: '#e2e8f0' } }}>
                  <EmailIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}

        <Divider sx={{ mb: 3 }} />

        {/* Interactive Star Rating */}
        <Typography variant="body1" fontWeight="700" color="text.primary" mb={1}>
          How would you rate your experience?
        </Typography>

        <Box display="flex" justifyContent="center" gap={1} mb={1}>
          {[1, 2, 3, 4, 5].map((star) => {
            const isFilled = (hoverRating || rating) >= star;
            return (
              <IconButton
                key={star}
                onClick={() => {
                  setRating(star);
                  if (star >= 4) setPrivateSubmitted(false);
                }}
                onMouseEnter={() => setHoverRating(star)}
                onMouseLeave={() => setHoverRating(0)}
                sx={{
                  p: 0.5,
                  transition: 'transform 0.15s ease-in-out',
                  '&:hover': { transform: 'scale(1.2)' },
                }}>
                <StarIcon
                  sx={{
                    fontSize: { xs: 40, sm: 46 },
                    color: isFilled ? '#fbbf24' : '#cbd5e1',
                    filter: isFilled ? 'drop-shadow(0 0 6px rgba(251, 191, 36, 0.6))' : 'none',
                  }}
                />
              </IconButton>
            );
          })}
        </Box>

        {/* Dynamic Sentiment Chip */}
        <Box mb={3}>
          <Chip
            label={
              rating === 5
                ? '🌟 Outstanding Experience!'
                : rating === 4
                ? '😊 Great Service!'
                : rating === 3
                ? '😐 Average Experience'
                : rating === 2
                ? '🙁 Needs Improvement'
                : '😞 Disappointing'
            }
            size="small"
            sx={{
              fontWeight: 700,
              fontSize: '0.75rem',
              bgcolor: rating >= 4 ? '#ecfdf5' : '#fffbeb',
              color: rating >= 4 ? '#047857' : '#b45309',
            }}
          />
        </Box>

        {/* ========================================================= */}
        {/* SINGLE PAGE FLOW 1: 4 & 5 STARS -> AI GOOGLE REVIEW FLOW */}
        {/* ========================================================= */}
        {rating >= 4 ? (
          <Fade in={true}>
            <Box textAlign="left">
              {/* Aspect Highlights */}
              <Typography variant="caption" fontWeight="700" color="text.secondary" display="block" mb={1}>
                TAP HIGHLIGHTS YOU ENJOYED:
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={0.8} mb={2}>
                {QUICK_TAGS.map((tag) => {
                  const isSelected = selectedTags.includes(tag);
                  return (
                    <Chip
                      key={tag}
                      label={tag}
                      size="small"
                      onClick={() => toggleTag(tag)}
                      sx={{
                        cursor: 'pointer',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        bgcolor: isSelected ? '#1976d2' : '#f1f5f9',
                        color: isSelected ? 'white' : '#475569',
                        '&:hover': { bgcolor: isSelected ? '#1565c0' : '#e2e8f0' },
                      }}
                    />
                  );
                })}
              </Box>

              {/* Tone Controls & Shuffle */}
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                <Box display="flex" gap={0.8}>
                  {TONE_OPTIONS.map((t) => (
                    <Chip
                      key={t.id}
                      label={t.label}
                      size="small"
                      variant={selectedTone === t.id ? 'filled' : 'outlined'}
                      color={selectedTone === t.id ? 'success' : 'default'}
                      onClick={() => handleToneChange(t.id)}
                      sx={{ fontSize: '0.7rem', fontWeight: 600 }}
                    />
                  ))}
                </Box>

                <Button
                  size="small"
                  startIcon={isGenerating ? <CircularProgress size={14} /> : <RefreshIcon />}
                  disabled={isGenerating}
                  onClick={() => handleGenerateAi()}
                  sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
                  Shuffle ✨
                </Button>
              </Box>

              {/* AI Draft Textarea */}
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2.5,
                  bgcolor: '#ffffff',
                  border: '1px solid #cbd5e1',
                  boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.03)',
                  mb: 2.5,
                }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <AutoAwesomeIcon sx={{ fontSize: 16, color: '#f59e0b' }} />
                    <Typography variant="caption" fontWeight="800" color="#f59e0b">
                      AI GENERATED 5-STAR DRAFT
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    Editable ✏️
                  </Typography>
                </Box>

                {isGenerating ? (
                  <Box py={3} textAlign="center">
                    <CircularProgress size={22} sx={{ mb: 1 }} />
                    <Typography variant="caption" display="block" color="text.secondary">
                      Crafting your genuine review...
                    </Typography>
                  </Box>
                ) : (
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    variant="standard"
                    value={reviewText}
                    onChange={(e) => setReviewText(e.target.value)}
                    InputProps={{ disableUnderline: true }}
                    sx={{ fontSize: '0.9rem' }}
                    placeholder="Your review draft appears here..."
                  />
                )}
              </Box>

              {/* High-Impact Primary Button: Copy & Post on Google */}
              <Button
                fullWidth
                size="large"
                variant="contained"
                onClick={handleCopyAndPost}
                disabled={isGenerating || !reviewText}
                startIcon={copied ? <CheckCircleIcon /> : <ContentCopyIcon />}
                endIcon={<OpenInNewIcon />}
                sx={{
                  py: 1.6,
                  borderRadius: 3,
                  fontWeight: 800,
                  fontSize: '1rem',
                  textTransform: 'none',
                  background: 'linear-gradient(135deg, #4285F4 0%, #1976D2 100%)',
                  boxShadow: '0 8px 20px rgba(66, 133, 244, 0.35)',
                  mb: 1.5,
                }}>
                {copied ? 'Review Copied! Opening Google...' : 'Copy Review & Post on Google'}
              </Button>

              {showRedirectNotice ? (
                <Alert severity="success" sx={{ borderRadius: 2 }}>
                  <Typography variant="caption" fontWeight="700" display="block">
                    Review Copied to Clipboard!
                  </Typography>
                  <Typography variant="caption">
                    Google Maps is opening. Just <strong>Paste</strong> and tap <strong>Post</strong>!
                  </Typography>
                  <Box mt={0.5}>
                    <a
                      href={googleReviewUrl || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(businessName)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#15803d' }}>
                      Didn't open? Click here ↗
                    </a>
                  </Box>
                </Alert>
              ) : (
                <Typography variant="caption" color="text.secondary" textAlign="center" display="block">
                  💡 Review copies automatically to clipboard when you tap the button.
                </Typography>
              )}
            </Box>
          </Fade>
        ) : (
          /* ========================================================= */
          /* SINGLE PAGE FLOW 2: 1-3 STARS -> PRIVATE MANAGEMENT FORM */
          /* ========================================================= */
          <Fade in={true}>
            <Box textAlign="left">
              {privateSubmitted ? (
                <Box textAlign="center" py={4}>
                  <CheckCircleIcon sx={{ fontSize: 48, color: '#10b981', mb: 1 }} />
                  <Typography variant="h6" fontWeight="800" color="text.primary" mb={1}>
                    Thank You For Your Feedback
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={3}>
                    Your feedback has been sent directly to the owner of <strong>{businessName}</strong>. We are committed to making things right and will contact you promptly.
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setPrivateSubmitted(false);
                      setRating(5);
                    }}
                    sx={{ textTransform: 'none', borderRadius: 2 }}>
                    Change Rating to 5-Star
                  </Button>
                </Box>
              ) : (
                <form onSubmit={handlePrivateSubmit}>
                  <Box mb={2}>
                    <Typography variant="subtitle2" fontWeight="700" color="text.primary">
                      How Can We Make It Right?
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      We're sorry you had a less than 5-star experience. Tell us what went wrong so the manager can follow up with you directly.
                    </Typography>
                  </Box>

                  <TextField
                    fullWidth
                    required
                    size="small"
                    label="Your Name"
                    value={privateName}
                    onChange={(e) => setPrivateName(e.target.value)}
                    sx={{ mb: 1.5 }}
                  />

                  <TextField
                    fullWidth
                    required
                    size="small"
                    label="Mobile / WhatsApp Number"
                    value={privatePhone}
                    onChange={(e) => setPrivatePhone(e.target.value)}
                    sx={{ mb: 1.5 }}
                  />

                  <TextField
                    fullWidth
                    size="small"
                    label="Email Address (optional)"
                    value={privateEmail}
                    onChange={(e) => setPrivateEmail(e.target.value)}
                    sx={{ mb: 1.5 }}
                  />

                  <TextField
                    fullWidth
                    required
                    multiline
                    rows={3}
                    size="small"
                    label="Please describe what happened..."
                    value={privateComment}
                    onChange={(e) => setPrivateComment(e.target.value)}
                    sx={{ mb: 2 }}
                  />

                  <Button
                    type="submit"
                    fullWidth
                    size="large"
                    variant="contained"
                    color="warning"
                    disabled={isSubmittingPrivate}
                    startIcon={isSubmittingPrivate ? <CircularProgress size={18} /> : <SendIcon />}
                    sx={{
                      py: 1.4,
                      borderRadius: 2.5,
                      fontWeight: 700,
                      textTransform: 'none',
                    }}>
                    {isSubmittingPrivate ? 'Sending to Management...' : 'Send Privately to Owner / Manager'}
                  </Button>
                </form>
              )}
            </Box>
          </Fade>
        )}
      </Paper>
    </Container>
  );
};

export default ReviewForm;
